/**
 * Priceless Possessions — keepsakes workspace / person overview section.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Gem } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { LoadingState } from '../ui/LoadingState';
import { PricelessPossessionDialog } from './PricelessPossessionDialog';
import { PricelessPossessionCard } from './PricelessPossessionCard';
import {
  deleteMemoryMarker,
  fetchMemoryMarkersForTree,
  filterPossessions,
  PRICELESS_POSSESSIONS_LABEL,
  PRICELESS_POSSESSIONS_TAGLINE,
  PRICELESS_POSSESSIONS_HEADLINE,
  PRICELESS_POSSESSIONS_NARRATIVE,
} from '../../utils/memoryMarkers';
import { isGenealogicalPerson } from '../../utils/companionProfiles';
import { toast } from 'sonner';
import { humanizeError } from '../../utils/errorHandling';

export function PricelessPossessionsSection({
  treeId,
  personId = null,
  people = [],
  stories = [],
  canEdit = false,
  compact = false,
  onChange,
  reloadToken = 0,
}) {
  const [markers, setMarkers] = useState([]);
  const [loading, setLoading] = useState(Boolean(treeId));
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const genealogicalPeople = useMemo(
    () => (people || []).filter((p) => isGenealogicalPerson(p)),
    [people],
  );

  const peopleById = useMemo(() => {
    const m = {};
    for (const p of people) m[p.id] = p;
    return m;
  }, [people]);

  const load = useCallback(async () => {
    if (!treeId) {
      setMarkers([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const rows = await fetchMemoryMarkersForTree(treeId, {
        personId: personId || null,
        possessionsOnly: true,
      });
      setMarkers(filterPossessions(rows));
    } catch {
      setMarkers([]);
    } finally {
      setLoading(false);
    }
  }, [treeId, personId]);

  useEffect(() => { load(); }, [load, reloadToken]);

  const possessions = useMemo(() => filterPossessions(markers), [markers]);

  const handleDelete = async (marker) => {
    if (!window.confirm(`Remove "${marker.title}" from your archive?`)) return;
    try {
      await deleteMemoryMarker(marker.id);
      toast.success('Keepsake removed.');
      await load();
      onChange?.();
    } catch (err) {
      toast.error(humanizeError(err));
    }
  };

  const empty = (
    <div className="rounded-xl border border-dashed border-[#c9a961]/35 bg-[#c9a961]/5 px-5 py-8 text-center">
      <Gem className="mx-auto mb-3 h-8 w-8 text-[#8b7355]/50" strokeWidth={1.25} />
      <p className="font-serif text-base text-[#1c1917]/85">Every object has a story.</p>
      <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-muted-foreground">
        Upload a photo of a keepsake, ornament, recipe book, piece of clothing, tool, toy, letter, or anything
        that carries a memory.
      </p>
      {canEdit ? (
        <Button type="button" className="mt-4 rounded-full" onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="add-priceless-possession-btn">
          <Plus className="mr-1.5 h-4 w-4" /> Add a priceless possession
        </Button>
      ) : null}
    </div>
  );

  const list = (
    <ul className={`space-y-3 ${compact ? '' : 'sm:grid sm:grid-cols-2 sm:gap-4 sm:space-y-0'}`}>
      {possessions.map((m) => (
        <PricelessPossessionCard
          key={m.id}
          marker={m}
          peopleById={peopleById}
          canEdit={canEdit}
          onEdit={(marker) => { setEditing(marker); setDialogOpen(true); }}
          onDelete={handleDelete}
          compact={compact}
        />
      ))}
    </ul>
  );

  if (compact) {
    return (
      <>
        <section className="rounded-xl border border-[hsl(38_14%_86%)]/80 bg-[hsl(42_28%_99%)]/90 p-4" data-testid="priceless-possessions-overview">
          <div className="mb-3 flex items-start justify-between gap-3">
            <div>
              <p className="font-serif text-base text-[#1c1917]">{PRICELESS_POSSESSIONS_LABEL}</p>
              <p className="mt-0.5 text-xs text-muted-foreground">Keepsakes connected to this person.</p>
            </div>
            {canEdit ? (
              <Button type="button" variant="outline" size="sm" className="shrink-0 rounded-full text-xs" onClick={() => { setEditing(null); setDialogOpen(true); }}>
                <Plus className="mr-1 h-3 w-3" /> Add
              </Button>
            ) : null}
          </div>
          {loading ? <LoadingState message="Loading…" variant="minimal" /> : null}
          {!loading && possessions.length === 0 ? (
            <p className="text-sm text-muted-foreground/80">A photo of something they cherished belongs here.</p>
          ) : null}
          {!loading && possessions.length > 0 ? list : null}
        </section>
        {canEdit ? (
          <PricelessPossessionDialog
            open={dialogOpen}
            onOpenChange={setDialogOpen}
            treeId={treeId}
            marker={editing}
            defaultPersonId={personId}
            genealogicalPeople={genealogicalPeople}
            stories={stories}
            onSaved={() => { load(); onChange?.(); }}
          />
        ) : null}
      </>
    );
  }

  return (
    <>
      <Card className="border-[#c9a961]/25 bg-gradient-to-br from-[hsl(42_28%_99%)] to-[hsl(38_22%_94%)]/30" data-testid="priceless-possessions-section">
        <CardHeader className="pb-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 font-serif text-xl">
                <Gem className="h-5 w-5 text-[#8b7355]" strokeWidth={1.25} />
                {PRICELESS_POSSESSIONS_LABEL}
              </CardTitle>
              <CardDescription className="mt-1 max-w-xl">{PRICELESS_POSSESSIONS_TAGLINE}</CardDescription>
              <p className="mt-2 max-w-xl text-sm leading-relaxed text-muted-foreground/85">{PRICELESS_POSSESSIONS_HEADLINE} {PRICELESS_POSSESSIONS_NARRATIVE}</p>
            </div>
            {canEdit ? (
              <Button type="button" className="shrink-0 rounded-full" onClick={() => { setEditing(null); setDialogOpen(true); }}>
                <Plus className="mr-1.5 h-4 w-4" /> Add a priceless possession
              </Button>
            ) : null}
          </div>
        </CardHeader>
        <CardContent>
          {loading ? <LoadingState message="Loading treasured things…" /> : null}
          {!loading && possessions.length === 0 ? empty : null}
          {!loading && possessions.length > 0 ? list : null}
        </CardContent>
      </Card>

      {canEdit ? (
        <PricelessPossessionDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          treeId={treeId}
          marker={editing}
          defaultPersonId={personId}
          genealogicalPeople={genealogicalPeople}
          stories={stories}
          onSaved={() => { load(); onChange?.(); }}
        />
      ) : null}
    </>
  );
}

export default PricelessPossessionsSection;
